/**
 * Validaciones de un archivo elegido de Drive (§8.5), con la metadata que
 * devuelve Drive: sin subir ni descargar nada.
 *
 * Decisiones de Claudio del 22/09/2026:
 *  · Se aceptan sin aviso 1:1, 4:5, 3:4 y 9:16 (±2 %). Cualquier otra
 *    proporción avisa en amarillo pero no bloquea: las diseñadoras entregan
 *    el tamaño correcto y el formato de Instagram cambia con el tiempo.
 *  · Video: MP4 y MOV. Se bloquea otro formato, más de 3 minutos o 500 MB.
 *  · Imagen: JPG, PNG o WEBP; se bloquea más de 15 MB.
 */

export type ResultadoValidacion =
  | { estado: 'ok' }
  | { estado: 'aviso'; mensaje: string }
  | { estado: 'rechazo'; mensaje: string }

export interface DatosArchivo {
  mime: string
  peso: number | null
  ancho: number | null
  alto: number | null
  duracionSeg: number | null
}

export const MB = 1_048_576
export const TOPE_IMAGEN = 15 * MB
export const TOPE_VIDEO = 500 * MB
export const TOPE_DURACION = 180

const IMAGENES = ['image/jpeg', 'image/png', 'image/webp']
const VIDEOS = ['video/mp4', 'video/quicktime']

/** Las proporciones de Instagram, ancho / alto. */
export const PROPORCIONES = [
  { nombre: '1:1', valor: 1 },
  { nombre: '4:5', valor: 4 / 5 },
  { nombre: '3:4', valor: 3 / 4 },
  { nombre: '9:16', valor: 9 / 16 },
] as const
const TOLERANCIA = 0.02

export const esVideo = (mime: string) => mime.startsWith('video/')
export const esImagen = (mime: string) => mime.startsWith('image/')

/** La proporción de Instagram que corresponde a esas medidas, o `null`. */
export function proporcionDe(ancho: number, alto: number): string | null {
  const r = ancho / alto
  const p = PROPORCIONES.find((x) => Math.abs(r - x.valor) / x.valor <= TOLERANCIA)
  return p?.nombre ?? null
}

const mb = (bytes: number) => `${(bytes / MB).toFixed(0)} MB`

export function validarArchivo(
  a: DatosArchivo,
  /** Qué se espera en ese lugar: la portada de un video es una imagen. */
  espera: 'imagen' | 'video' | 'imagen-o-video',
): ResultadoValidacion {
  if (esVideo(a.mime)) {
    if (espera === 'imagen') {
      return { estado: 'rechazo', mensaje: 'Acá va una imagen, no un video.' }
    }
    if (!VIDEOS.includes(a.mime)) {
      return {
        estado: 'rechazo',
        mensaje: 'Ese formato de video no se puede publicar. Usá MP4 o MOV.',
      }
    }
    if (a.peso !== null && a.peso > TOPE_VIDEO) {
      return {
        estado: 'rechazo',
        mensaje: `El video pesa ${mb(a.peso)}: el máximo es 500 MB.`,
      }
    }
    if (a.duracionSeg !== null && a.duracionSeg > TOPE_DURACION) {
      const min = Math.floor(a.duracionSeg / 60)
      const seg = String(a.duracionSeg % 60).padStart(2, '0')
      return {
        estado: 'rechazo',
        mensaje: `El video dura ${String(min)}:${seg}: el máximo es 3 minutos.`,
      }
    }
  } else if (esImagen(a.mime)) {
    if (espera === 'video') {
      return { estado: 'rechazo', mensaje: 'Acá va un video, no una imagen.' }
    }
    if (!IMAGENES.includes(a.mime)) {
      return {
        estado: 'rechazo',
        mensaje: 'Ese formato de imagen no se puede publicar. Usá JPG o PNG.',
      }
    }
    if (a.peso !== null && a.peso > TOPE_IMAGEN) {
      return {
        estado: 'rechazo',
        mensaje: `La imagen pesa ${mb(a.peso)}: el máximo es 15 MB.`,
      }
    }
  } else {
    return {
      estado: 'rechazo',
      mensaje: 'Ese archivo no es una imagen ni un video.',
    }
  }

  if (a.ancho && a.alto && !proporcionDe(a.ancho, a.alto)) {
    return {
      estado: 'aviso',
      mensaje: `Esta pieza mide ${String(a.ancho)}×${String(a.alto)}: no es una proporción de Instagram (1:1, 4:5, 3:4 o 9:16). Se puede cargar igual.`,
    }
  }
  return { estado: 'ok' }
}
